<template>
  <div class="messages-container flex-1 p-4 overflow-y-auto bg-surface">
    <div v-if="loading" class="flex justify-center py-4">
      <div class="loading-spinner"></div>
    </div>
    
    <template v-else>
      <div 
        v-for="message in messages" 
        :key="message.id" 
        class="message-wrapper mb-4"
        :class="{ 'message-user': message.senderType === 'user', 'message-bot': message.senderType === 'bot', 'message-system': message.senderType === 'system' }"
      >
        <!-- System messages (centered) -->
        <div v-if="message.senderType === 'system'" class="flex justify-center">
          <div class="system-message text-sm py-1 px-3 rounded-full bg-gray-200 text-gray-600 inline-block">
            {{ message.content }}
          </div>
        </div>
        
        <!-- User messages (right aligned) -->
        <div v-else-if="message.senderType === 'user'" class="flex flex-col items-end">
          <div class="message-bubble user-bubble">
            <!-- Text message -->
            <div 
              v-if="message.type === 'text'" 
              v-html="renderMarkdown(message.content)"
              class="message-content"
            ></div>
            
            <!-- Image message -->
            <div v-else-if="message.type === 'image'" class="image-message">
              <img 
                :src="message.metadata?.fileUrl" 
                :alt="message.metadata?.fileName || 'Image'"
                class="max-w-full rounded-lg max-h-60 object-contain"
                @click="openImageModal(message.metadata?.fileUrl)"
              />
            </div>
            
            <!-- File message -->
            <div v-else-if="message.type === 'file'" class="file-message">
              <div class="file-icon">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div class="file-details">
                <div class="file-name text-sm font-medium truncate">{{ message.metadata?.fileName }}</div>
                <div class="file-size text-xs text-gray-500">
                  {{ formatFileSize(message.metadata?.fileSize) }}
                </div>
              </div>
              <a 
                :href="message.metadata?.fileUrl" 
                download 
                class="file-download-btn"
                target="_blank"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
              </a>
            </div>
            
            <!-- Audio message -->
            <div v-else-if="message.type === 'audio'" class="audio-message">
              <audio controls class="w-full">
                <source :src="message.metadata?.fileUrl" :type="message.metadata?.fileType">
                Your browser does not support the audio element.
              </audio>
            </div>
            
            <!-- Location message -->
            <div v-else-if="message.type === 'location'" class="location-message">
              <div class="location-name text-sm font-medium mb-1">
                {{ message.metadata?.locationName || 'Shared location' }}
              </div>
              <div class="location-preview">
                <!-- This would typically be a map preview - using a placeholder for now -->
                <div class="map-placeholder">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <a 
                  :href="`https://maps.google.com/?q=${message.metadata?.latitude},${message.metadata?.longitude}`" 
                  target="_blank"
                  class="view-on-map"
                >
                  View on map
                </a>
              </div>
            </div>
          </div>
          
          <!-- User message status - moved outside bubble -->
          <div class="message-info flex items-center justify-end mt-1 gap-2">
            <div class="message-timestamp text-xs text-gray-500">
              {{ formatTimestamp(message.timestamp) }}
            </div>
            <div v-if="message.senderType === 'user'" class="message-status">
              <span v-if="message.status === 'sending'" class="status-indicator text-xs text-gray-500">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 inline mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Sending
              </span>
              <span v-else-if="message.status === 'sent'" class="status-indicator text-xs text-gray-500">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
              </span>
              <span v-else-if="message.status === 'delivered'" class="status-indicator text-xs text-gray-500">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7M5 13l4 4L19 7" />
                </svg>
              </span>
              <span v-else-if="message.status === 'read'" class="status-indicator text-xs text-blue-500">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7M5 13l4 4L19 7" />
                </svg>
              </span>
              <span v-else-if="message.status === 'error'" class="status-indicator text-xs text-red-500">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 inline mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Failed
              </span>
            </div>
          </div>
        </div>
        
        <!-- Bot messages (left aligned) -->
        <div v-else class="flex">
          <div class="avatar-container mr-2 mt-1">
            <img 
              src="/orbitar_chat_logo.png" 
              alt="Orbitar AI"
              class="h-6 w-6 rounded-full object-cover"
            />
          </div>
          <div class="flex flex-col">
            <div class="message-bubble bot-bubble">
              <!-- Text message -->
              <div 
                v-if="message.type === 'text'" 
                v-html="renderMarkdown(message.content)"
                class="message-content"
              ></div>
              
              <!-- Other message types would be handled similarly to user messages -->
              <div v-else-if="message.type === 'image'" class="image-message">
                <img 
                  :src="message.metadata?.fileUrl" 
                  :alt="message.metadata?.fileName || 'Image'"
                  class="max-w-full rounded-lg max-h-60 object-contain"
                />
              </div>
              
              <!-- File message -->
              <div v-else-if="message.type === 'file'" class="file-message">
                <!-- Similar to user file message -->
              </div>
            </div>
            
            <!-- Bot message timestamp -->
            <div class="message-timestamp text-xs text-gray-500 ml-1 mt-1">
              {{ formatTimestamp(message.timestamp) }}
            </div>
          </div>
        </div>
      </div>
      
      <!-- Typing indicator for bot -->
      <div v-if="botIsTyping" class="typing-indicator-container flex mt-2 mb-4">
        <div class="avatar-container mr-2 mt-1">
          <img 
            src="/orbitar_chat_logo.png" 
            alt="Orbitar AI"
            class="h-8 w-8 rounded-full object-cover"
          />
        </div>
        <div class="typing-indicator">
          <div class="typing-bubble"></div>
          <div class="typing-bubble"></div>
          <div class="typing-bubble"></div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, nextTick, computed } from 'vue';
import { socketService } from '../services/socket.service';
import type { Message } from '../store/messages';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

const props = defineProps<{
  messages: Message[];
  loading: boolean;
  userId: string;
}>();

// Local reference to the bot typing status
const botIsTyping = ref(false);
const messagesContainer = ref<HTMLElement | null>(null);

// Update local botIsTyping ref when socket service updates
watch(() => socketService.botIsTyping.value, (newValue) => {
  botIsTyping.value = newValue;
});

// Configure marked for safe rendering
marked.setOptions({
  breaks: false, // Don't convert single line breaks to <br> to allow proper list parsing
  gfm: true, // Enable GitHub Flavored Markdown
});

// Method to safely render markdown content
const renderMarkdown = (content: string): string => {
  if (!content) return '';
  
  try {
    // Convert markdown to HTML
    const htmlContent = marked(content);
    
    // Sanitize HTML to prevent XSS attacks
    return DOMPurify.sanitize(htmlContent as string, {
      ALLOWED_TAGS: [
        'p', 'br', 'strong', 'em', 'u', 'code', 'pre', 'blockquote',
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'ul', 'ol', 'li',
        'a', 'img',
        'table', 'thead', 'tbody', 'tr', 'th', 'td',
        'del', 'ins', 'sub', 'sup'
      ],
      ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'target', 'rel', 'type', 'start']
    });
  } catch (error) {
    console.error('Error rendering markdown:', error);
    return content; // Fallback to plain text
  }
};

// Method to format file size
const formatFileSize = (bytes?: number): string => {
  if (!bytes) return 'Unknown size';
  
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;
  
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }
  
  return `${size.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

// Method to format timestamp
const formatTimestamp = (timestamp: number): string => {
  if (!timestamp) return '';
  
  const date = new Date(timestamp);
  const now = new Date();
  
  // Format: HH:MM
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const timeString = `${hours}:${minutes}`;
  
  // For messages from today, just show time
  if (date.toDateString() === now.toDateString()) {
    return timeString;
  }
  
  // For messages from yesterday, show "Yesterday, HH:MM"
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) {
    return `Yesterday, ${timeString}`;
  }
  
  // For other dates, show DD/MM/YYYY, HH:MM
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  
  return `${day}/${month}/${year}, ${timeString}`;
};

// Method to scroll to bottom of messages
const scrollToBottom = () => {
  if (messagesContainer.value) {
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
  }
};

// Image modal functionality
const currentImage = ref<string | null>(null);

const openImageModal = (url?: string) => {
  if (url) {
    currentImage.value = url;
    // In a real implementation, would open a modal to show the image in full size
  }
};

const closeImageModal = () => {
  currentImage.value = null;
};

// Watch for new messages and scroll to bottom
watch(() => props.messages.length, async () => {
  await nextTick();
  scrollToBottom();
});

// Scroll to bottom on mount and connect socket
onMounted(() => {
  messagesContainer.value = document.querySelector('.messages-container');
  scrollToBottom();
});
</script>

<style scoped>
.messages-container {
  scroll-behavior: smooth;
  background-image: url('/orbitar_chat_default_background.png');
  background-size: cover;
  background-position: center;
}

.message-bubble {
  max-width: 80%;
  padding: 8px 12px;
  border-radius: 16px;
  position: relative;
  word-break: break-word;
}

.user-bubble {
  background-color: var(--color-primary);
  color: white;
  border-bottom-right-radius: 4px;
}

.bot-bubble {
  background-color: #ffffff;
  color: #333;
  border-bottom-left-radius: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.system-message {
  margin: 8px 0;
  background-color: rgba(255, 255, 255, 0.8);
}

.message-info {
  font-size: 10px;
  opacity: 0.8;
}

.message-timestamp {
  font-size: 10px;
  opacity: 0.8;
}

.file-message {
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 8px;
}

.file-icon {
  margin-right: 8px;
}

.file-details {
  flex: 1;
  min-width: 0;
}

.file-download-btn {
  margin-left: 8px;
}

.map-placeholder {
  background: #f0f0f0;
  border-radius: 8px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.typing-indicator-container {
  align-items: flex-start;
}

.typing-indicator {
  display: flex;
  padding: 8px 12px;
  background-color: #ffffff;
  border-radius: 16px;
  border-bottom-left-radius: 4px;
  justify-content: center;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.typing-bubble {
  width: 8px;
  height: 8px;
  margin: 0 2px;
  background-color: #b6b6b6;
  border-radius: 50%;
  animation: typing-bubble 1s infinite ease-in-out;
}

.typing-bubble:nth-child(1) {
  animation-delay: 0s;
}

.typing-bubble:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-bubble:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typing-bubble {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-5px);
  }
}

.loading-spinner {
  border: 3px solid #f3f3f3;
  border-radius: 50%;
  border-top: 3px solid var(--color-primary);
  width: 24px;
  height: 24px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Adjusted the avatar-container to reduce the icon size */
.avatar-container {
  min-width: 40px; /* Reduced the minimum width */
  display: flex;
  justify-content: center;
  align-items: center;
}

.avatar-container img {
  height: 2rem; /* Reduced the height */
  width: 2rem; /* Reduced the width */
}

/* Markdown content styling */
.message-content {
  line-height: 1.5;
}

.message-content p {
  margin: 0 0 8px 0;
}

.message-content p:last-child {
  margin-bottom: 0;
}

.message-content strong {
  font-weight: 600;
}

.message-content em {
  font-style: italic;
}

.message-content code {
  background-color: rgba(0, 0, 0, 0.1);
  padding: 2px 4px;
  border-radius: 3px;
  font-family: 'Courier New', monospace;
  font-size: 0.9em;
}

.message-content pre {
  background-color: rgba(0, 0, 0, 0.1);
  padding: 8px;
  border-radius: 6px;
  margin: 8px 0;
  overflow-x: auto;
}

.message-content pre code {
  background-color: transparent;
  padding: 0;
}

.message-content blockquote {
  border-left: 3px solid rgba(0, 0, 0, 0.2);
  padding-left: 12px;
  margin: 8px 0;
  font-style: italic;
}

.message-content h1, .message-content h2, .message-content h3,
.message-content h4, .message-content h5, .message-content h6 {
  margin: 12px 0 8px 0;
  font-weight: 600;
}

.message-content h1 { font-size: 1.4em; }
.message-content h2 { font-size: 1.3em; }
.message-content h3 { font-size: 1.2em; }
.message-content h4 { font-size: 1.1em; }
.message-content h5 { font-size: 1.05em; }
.message-content h6 { font-size: 1em; }




.message-content a {
  color: inherit;
  text-decoration: underline;
}

.message-content table {
  border-collapse: collapse;
  width: 100%;
  margin: 8px 0;
}

.message-content th, .message-content td {
  border: 1px solid rgba(0, 0, 0, 0.2);
  padding: 4px 8px;
  text-align: left;
}

.message-content th {
  background-color: rgba(0, 0, 0, 0.05);
  font-weight: 600;
}

/* Different colors for user vs bot content */
.user-bubble .message-content code {
  background-color: rgba(255, 255, 255, 0.2);
}

.user-bubble .message-content pre {
  background-color: rgba(255, 255, 255, 0.2);
}

.user-bubble .message-content blockquote {
  border-left-color: rgba(255, 255, 255, 0.4);
}

.user-bubble .message-content th, .user-bubble .message-content td {
  border-color: rgba(255, 255, 255, 0.3);
}

.user-bubble .message-content th {
  background-color: rgba(255, 255, 255, 0.1);
}
</style>